import React from 'react';
import { Building2, Briefcase, AlertCircle, CheckCircle, LogIn, LogOut, Moon, Sun } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
} from '../ui';
import type { TimeRecord } from '../../types/time-records.types';
import { formatDate, formatTime } from '../../utils/dateFormat';
import { formatSeconds, formatMinutes, getLateColorClass, getOvertimeColorClass } from '../../utils/timeFormat';

interface TimeRecordsTableProps {
  records: TimeRecord[];
}

export function TimeRecordsTable({ records }: TimeRecordsTableProps) {
  const getEmployeeName = (record: TimeRecord) => {
    if (!record.employee) return 'Не указан';
    const { first_name, last_name, middle_name } = record.employee;
    return [last_name, first_name, middle_name].filter(Boolean).join(' ');
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Дата</TableHead>
          <TableHead>Смена</TableHead>
          <TableHead>Сотрудник</TableHead>
          <TableHead>Время входа</TableHead>
          <TableHead>Время выхода</TableHead>
          <TableHead>Отработано</TableHead>
          <TableHead>Перерывы</TableHead>
          <TableHead>Сверхурочные</TableHead>
          <TableHead>Опоздание</TableHead>
          <TableHead>Ранний уход</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {records.map((record) => {
          const workDate = formatDate(record.work_date);

          return (
            <TableRow key={record.id}>
              {/* Work Date */}
              <TableCell>
                <div className="font-medium">{workDate}</div>
              </TableCell>

              {/* Shift Kind */}
              <TableCell>
                {record.is_night_shift ? (
                  <Badge variant="default" className="bg-indigo-100 text-indigo-800">
                    <Moon className="h-3 w-3 mr-1" />
                    Ночная
                  </Badge>
                ) : (
                  <Badge variant="default" className="bg-amber-100 text-amber-800">
                    <Sun className="h-3 w-3 mr-1" />
                    Дневная
                  </Badge>
                )}
              </TableCell>

              {/* Employee */}
              <TableCell>
                <div className="space-y-1">
                  <p className="font-medium">{getEmployeeName(record)}</p>
                  {record.employee && (
                    <div className="flex flex-col text-sm text-muted-foreground">
                      {record.employee.employee_number && (
                        <span>№{record.employee.employee_number}</span>
                      )}
                      <div className="flex items-center gap-3 mt-1">
                        {record.employee.department && (
                          <div className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            <span className="text-xs">{record.employee.department.name}</span>
                          </div>
                        )}
                        {record.employee.position && (
                          <div className="flex items-center gap-1">
                            <Briefcase className="h-3 w-3" />
                            <span className="text-xs">{record.employee.position.name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </TableCell>

              {/* Check In Time */}
              <TableCell>
                {record.check_in_time ? (
                  <div className="flex items-center gap-1 text-green-600">
                    <LogIn className="h-4 w-4" />
                    <span className="font-medium">{formatTime(record.check_in_time)}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>

              {/* Check Out Time */}
              <TableCell>
                {record.check_out_time ? (
                  <div className="flex items-center gap-1 text-red-600">
                    <LogOut className="h-4 w-4" />
                    <span className="font-medium">{formatTime(record.check_out_time)}</span>
                  </div>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>

              {/* Worked Time */}
              <TableCell>
                <div className="font-medium text-blue-600">
                  {formatSeconds(record.worked_seconds)}
                </div>
              </TableCell>

              {/* Break Time */}
              <TableCell>
                <div className="text-muted-foreground">
                  {formatSeconds(record.break_seconds)}
                </div>
              </TableCell>

              {/* Overtime */}
              <TableCell>
                <div className={getOvertimeColorClass(record.overtime_seconds)}>
                  {record.overtime_seconds > 0 ? (
                    <div className="font-medium">
                      {formatSeconds(record.overtime_seconds)}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </div>
              </TableCell>

              {/* Late */}
              <TableCell>
                {record.is_late ? (
                  <div className="flex items-center gap-2">
                    <Badge variant="default" className="bg-yellow-100 text-yellow-800">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {formatMinutes(record.late_minutes)}
                    </Badge>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Вовремя</span>
                  </div>
                )}
              </TableCell>

              {/* Early Leave */}
              <TableCell>
                {record.left_early ? (
                  <div className="flex items-center gap-2">
                    <Badge variant="default" className="bg-red-100 text-red-800">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {formatMinutes(record.early_leave_minutes)}
                    </Badge>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Полный день</span>
                  </div>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
